import { Head, usePage } from "@inertiajs/react";
import { useForm } from "@inertiajs/react";
import Loader from "@/Components/Loader";
import React, { useState, useEffect } from "react";

const CONTAINER_ID = "recaptcha";
const SKIN_TYPES = [
    {
        label: "SI",
        value: true,
    },
    {
        label: "NO",
        value: false,
    },
];

/**
 * Generate an array of years starting from 1950 up to (current year - 10)
 *
 * @returns {number[]} An array of years
 */
const generateYears = () => {
    const years = [];
    const maxYear = new Date().getFullYear() - 10;
    for (let year = 1950; year <= maxYear; year++) {
        years.push(year);
    }
    return years.reverse();
};

/**
 * Calculate the number of days in a given month and year
 *
 * @param {number} month The month number (1-12)
 * @param {number} year The year number
 * @returns {number} The number of days in the specified month
 */
const daysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
};

/**
 * Concern button component
 *
 * @param {Object} props
 * @param {Object} props.concern
 * @param {Object} props.selectedConcern
 * @param {Function} props.setSelectedConcern
 * @param {Number} props.index
 * @returns {JSX.Element}
 */
const ConcernButton = ({ concern, selectedConcern, setSelectedConcern }) => {
    return (
        <button
            type="button"
            onClick={() => {
                setSelectedConcern(concern);
            }}
            dusk={`concern-button-${concern.slug}`}
            className="cursor-pointer flex items-center h-10 mr-6"
        >
            <div className="bg-white w-5 h-5 rounded-full border border-primary-400 flex items-center justify-center">
                {selectedConcern?.name === concern.name && (
                    <div className="bg-primary-400 rounded-full w-3 h-3"></div>
                )}
            </div>
            <div className="ml-3">
                <p
                    className="text-left text-base pl-2 md:pl-0 md:text-center leading-none md:leading-normal font-bold uppercase"
                    dusk={`concern-name-${concern.slug}`}
                >
                    {concern.name} {concern.score}/10
                </p>
            </div>
        </button>
    );
};

/**
 * FormComponent is a functional React component that renders a form
 * to collect the user's personal information
 *
 * @returns
 */
export default function FormComponent({ concerns, results }) {
    const { use_slim_version } = usePage().props;
    const [monthDays, setMonthDays] = useState(31);
    const [selectedSkinType, setSelectedSkinType] = useState(SKIN_TYPES[0]);
    const [selectedConcern, setSelectedConcern] = useState(
        concerns[0] || null,
    );

    const form = useForm({
        email: "",
        gender: "",
        terms: true,
        skin_type: null,
        upload_id: null,
        results: results.results || {},
        concern: concerns[0] || null,
        birthday: { day: "", month: "", year: "" },
        snapshot: results.snapshot,
        snapshot_with_score: results.snapshotWithScore,
    });

    useEffect(() => {
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({
            event: "form_start",
            eventCategory: "SkinTest",
            eventAction: "Resultados",
            eventLabel: "Resultados preliminares",
        });
    }, []);

    useEffect(() => {
        form.setData("concern", selectedConcern);
    }, [selectedConcern]);

    useEffect(() => {
        form.setData("skin_type", selectedSkinType.value);
    }, [selectedSkinType]);

    /**
     * Handle the month value change in the birthday field
     * and adjust the day options accordingly
     *
     * @param {Event} e Change event
     */
    const handleMonthChange = (e) => {
        const newMonth = e.target.value;
        form.setData("birthday", { ...form.data.birthday, month: newMonth });
        const newDays = daysInMonth(newMonth, form.data.birthday.year);
        setMonthDays(newDays);
    };

    /**
     * Handle form submit, transform the form data
     * and send a POST request
     *
     * @param {Event} e Submit event
     */
     function handleSubmit(e) {
        e.preventDefault();

        form.post(route("skincare.storeForm"), {
            preserveScroll: true,
            onSuccess: async () => {
                window.dataLayer = window.dataLayer || [];
                dataLayer.push({
                    event: "CompleteForm",
                    eventCategory: "SkinTest",
                    eventAction: "Resultados",
                    eventLabel: "Resultados preliminares",
                });
            },
        });
    }

    return (
        <>
            <Head title="Formulario" />
            <div className="bg-[#e4eefc] px-3 pb-12 pt-6 w-full mb-12">
                <h1 className="text-3xl font-bold mb-4 uppercase text-center">
                    ¿A donde enviamos tus resultados?
                </h1>
                <p className="mb-6 text-center text-lg leading-tight text-[#666666] max-w-md mx-auto">
                    Llena el siguiente formulario para enviarte los resultados
                    por email de acuerdo a tu perfil y aspecto a mejorar
                </p>

                <form
                    onSubmit={handleSubmit}
                    dusk="form"
                    className="flex flex-col space-y-4 max-w-lg mx-auto"
                >
                    <div className="flex flex-col">
                        <label htmlFor="email" className="font-bold">
                            Email *
                        </label>
                        <input
                            className="bg-white border border-gray-300"
                            type="email"
                            name="email"
                            id="email"
                            dusk="email"
                            value={form.data.email}
                            onChange={(e) =>
                                form.setData("email", e.target.value)
                            }
                        />
                        {form.errors.email && (
                            <div className="text-red-400 block mt-1 font-medium">
                                {form.errors.email}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="birthday" className="font-bold">
                            Fecha de nacimiento *
                        </label>
                        <div className="grid grid-cols-3 gap-4 flex-1">
                            <div className="flex flex-col">
                                <select
                                    className="bg-white border border-gray-300"
                                    id="year"
                                    dusk="year"
                                    value={form.data.birthday.year}
                                    onChange={(e) =>
                                        form.setData("birthday", {
                                            ...form.data.birthday,
                                            year: e.target.value,
                                        })
                                    }
                                    aria-label="Año"
                                >
                                    <option disabled value="">
                                        AAAA
                                    </option>
                                    {generateYears().map((year) => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </select>
                                {form.errors["birthday.year"] && (
                                    <div className="text-red-400 block mt-1 font-medium">
                                        {form.errors["birthday.year"]}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col">
                                <select
                                    className="bg-white border border-gray-300"
                                    id="month"
                                    dusk="month"
                                    value={form.data.birthday.month}
                                    onChange={handleMonthChange}
                                    aria-label="Mes"
                                >
                                    <option disabled value="">
                                        MM
                                    </option>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <option key={i} value={i + 1}>
                                            {i + 1}
                                        </option>
                                    ))}
                                </select>
                                {form.errors["birthday.month"] && (
                                    <div className="text-red-400 block mt-1 font-medium">
                                        {form.errors["birthday.month"]}
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col">
                                <select
                                    className="bg-white border border-gray-300"
                                    id="day"
                                    dusk="day"
                                    value={form.data.birthday.day}
                                    onChange={(e) =>
                                        form.setData("birthday", {
                                            ...form.data.birthday,
                                            day: e.target.value,
                                        })
                                    }
                                    aria-label="Día"
                                >
                                    <option disabled value="">
                                        DD
                                    </option>
                                    {Array.from(
                                        { length: monthDays },
                                        (_, i) => (
                                            <option key={i} value={i + 1}>
                                                {i + 1}
                                            </option>
                                        ),
                                    )}
                                </select>
                                {form.errors["birthday.day"] && (
                                    <div className="text-red-400 block mt-1 font-medium">
                                        {form.errors["birthday.day"]}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <label htmlFor="gender" className="font-bold">
                            Género *
                        </label>
                        <select
                            className="bg-white border border-gray-300"
                            id="gender"
                            dusk="gender"
                            value={form.data.gender}
                            onChange={(e) =>
                                form.setData("gender", e.target.value)
                            }
                            aria-label="Género"
                        >
                            <option value="" disabled>
                                Selecciona un género
                            </option>
                            <option value="male">Masculino</option>
                            <option value="female">Femenino</option>
                        </select>
                        {form.errors.gender && (
                            <div className="text-red-400 block mt-1 font-medium">
                                {form.errors.gender}
                            </div>
                        )}
                    </div>

                    <div>
                        <label htmlFor="gender" className="font-bold">
                            Que aspecto de tu piel te gustaría mejorar?
                        </label>
                        <div
                            className="flex flex-wrap w-full max-w-4xl mt-3"
                            dusk="concern-buttons"
                        >
                            {concerns.map((result, index) => (
                                <ConcernButton
                                    key={index}
                                    index={index}
                                    concern={result}
                                    selectedConcern={selectedConcern}
                                    setSelectedConcern={setSelectedConcern}
                                />
                            ))}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="gender" className="font-bold">
                            Consideras que tienes piel sensible?
                        </label>
                        <div
                            className="flex flex-row space-x-6 w-full mt-3"
                            dusk="skin-type-cards"
                        >
                            {SKIN_TYPES.map((skinType, index) => {
                                return (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() =>
                                            setSelectedSkinType(skinType)
                                        }
                                        className={`h-10 flex items-center justify-center font-bold text-lg`}
                                    >
                                        <div className="bg-white w-5 h-5 rounded-full border border-primary-400 flex items-center justify-center mr-3">
                                            {selectedSkinType.value ===
                                                skinType.value && (
                                                <div className="bg-primary-400 rounded-full w-3 h-3"></div>
                                            )}
                                        </div>
                                        {skinType.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <div>
                            <input
                                className="mr-2"
                                type="checkbox"
                                name="terms"
                                dusk="terms"
                                id="terms"
                                checked={form.data.terms}
                                onChange={(e) =>
                                    form.setData("terms", e.target.checked)
                                }
                            />
                            <label htmlFor="terms" className="font-medium">
                                Acepto el uso de mis datos personales de acuerdo
                                a los{" "}
                                <a
                                    href="https://bassa.com.ec/terminos-y-condiciones/"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary-400 hover:underline"
                                >
                                    términos y condiciones
                                </a>{" "}
                                de Bassa para enviar materiales informativos y
                                mis resultados de la evaluación.
                            </label>
                        </div>
                        {form.errors.terms && (
                            <div className="text-red-400 block mt-1 font-medium">
                                {form.errors.terms}
                            </div>
                        )}
                    </div>

                    {!use_slim_version && (
                        <div className="flex flex-col items-center justify-center mt-6">
                            <div id={CONTAINER_ID} />
                            {form.errors.g_recaptcha_response && (
                                <div className="text-red-400 block mt-1 font-medium">
                                    {form.errors.g_recaptcha_response}
                                </div>
                            )}
                        </div>
                    )}

                    <div
                        className="flex justify-center mt-6 fixed w-full p-4 bottom-0 left-0 z-20 lg:relative"
                        id="submit"
                    >
                        {form.processing ? (
                            <button
                                dusk="submit"
                                className="bg-black disabled:cursor-wait text-white flex justify-center text-lg px-6 py-4 lg:py-3 font-semibold w-full mx-auto"
                                type="submit"
                                disabled={true}
                            >
                                <Loader className="h-6 w-6 text-white" />
                            </button>
                        ) : (
                            <button
                                dusk="submit"
                                className="bg-black text-white text-lg px-6 py-4 lg:py-3 font-semibold w-full mx-auto"
                                type="submit"
                            >
                                ENVIAR Y VER RESULTADOS
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </>
    );
}
